/**
 * Vanilla role card grid
 *
 * Drives the "Which best describes you?" radiogroup on the two registration
 * forms: a 2-column grid of radio cards, each a FontAwesome mark beside a
 * single-line label. The cards are server-rendered from
 * `RegistrationProfileOptions::ROLES`, so this module only carries behavior —
 * never the role list itself.
 *
 * The value written to the hidden input is always the stable kebab-case id
 * ("solo-trainer"), never the display label.
 *
 * Keyboard model follows the WAI-ARIA radiogroup pattern: roving tabindex,
 * arrow keys move AND select, Space/Enter selects the focused card.
 *
 * Markup contract (see app/views/users/shared/_registration_profile_fields.html.erb):
 *   [data-role-grid]     the role="radiogroup" container
 *     [data-role-option] each role="radio" card, carrying data-value
 *       [data-role-radio]     the radio circle (optional)
 *       [data-role-radio-dot] the filled centre of a selected radio (optional)
 *   [data-role-value]    the hidden input carrying the id
 *
 * Selected/unselected card, circle and dot styling is repainted here; the
 * leading mark is a colour emoji that carries its own palette, so it needs no
 * hook of its own.
 */

export interface RoleGridOptions {
  /** Called with the newly selected role id whenever the selection changes. */
  onChange?: (roleId: string) => void;
}

export interface RoleGridHandle {
  /** The currently selected role id, or an empty string when none is picked. */
  selectedRole: () => string;
  /** Tear down every listener this grid registered. */
  destroy: () => void;
}

/**
 * Mount the role grid found inside a scope.
 *
 * @param scope - the element or document containing the grid
 * @param options - optional `onChange` callback
 * @returns a handle, or `null` when the page renders no role grid
 */
export function mountRoleGrid(
  scope: ParentNode = document,
  options: RoleGridOptions = {},
): RoleGridHandle | null {
  const grid = scope.querySelector<HTMLElement>("[data-role-grid]");
  const hiddenInput =
    scope.querySelector<HTMLInputElement>("[data-role-value]");
  if (!grid || !hiddenInput) return null;

  const valueInput: HTMLInputElement = hiddenInput;
  const cards = Array.from(
    grid.querySelectorAll<HTMLElement>("[data-role-option]"),
  );
  if (cards.length === 0) return null;

  /**
   * Paint every card for the given selection and move the roving tabindex onto
   * the selected card (or the first card when nothing is selected yet).
   *
   * @param roleId - the selected role id, or an empty string
   */
  function paint(roleId: string): void {
    const hasSelection = cards.some((card) => card.dataset.value === roleId);

    cards.forEach((card, index) => {
      const selected = card.dataset.value === roleId;
      card.setAttribute("aria-checked", selected ? "true" : "false");
      card.tabIndex = selected || (!hasSelection && index === 0) ? 0 : -1;

      card.classList.toggle("border-primary-500", selected);
      card.classList.toggle("bg-tint-primary-bg-soft", selected);
      card.classList.toggle("border-border-default", !selected);
      card.classList.toggle("bg-surface-card", !selected);
      // Hover only tints an unselected card, so it can never win over the
      // selected card's primary border.
      card.classList.toggle("hover:border-primary-300", !selected);

      const radio = card.querySelector<HTMLElement>("[data-role-radio]");
      radio?.classList.toggle("border-primary-500", selected);
      radio?.classList.toggle("border-border-default", !selected);

      const dot = card.querySelector<HTMLElement>("[data-role-radio-dot]");
      if (dot) dot.hidden = !selected;
    });
  }

  /**
   * Select a card: write its id to the hidden input, repaint, and notify.
   *
   * @param card - the card to select
   * @param focus - whether to move DOM focus onto the card
   */
  function select(card: HTMLElement, focus: boolean): void {
    const roleId = card.dataset.value ?? "";
    valueInput.value = roleId;
    paint(roleId);
    if (focus) card.focus();
    options.onChange?.(roleId);
  }

  /**
   * Move the selection by an offset, wrapping at both ends.
   *
   * @param from - the card the user is currently on
   * @param delta - +1 for Down/Right, -1 for Up/Left
   */
  function step(from: HTMLElement, delta: number): void {
    const index = cards.indexOf(from);
    if (index === -1) return;
    select(cards[(index + delta + cards.length) % cards.length], true);
  }

  const onClick = (event: MouseEvent) => {
    const card = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-role-option]",
    );
    if (card) select(card, true);
  };

  const onKeydown = (event: KeyboardEvent) => {
    const card = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-role-option]",
    );
    if (!card) return;

    switch (event.key) {
      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        step(card, 1);
        return;
      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        step(card, -1);
        return;
      case " ":
      case "Enter":
        event.preventDefault();
        select(card, true);
        return;
      default:
    }
  };

  grid.addEventListener("click", onClick);
  grid.addEventListener("keydown", onKeydown);

  paint(valueInput.value);

  return {
    selectedRole: () => valueInput.value,
    destroy: () => {
      grid.removeEventListener("click", onClick);
      grid.removeEventListener("keydown", onKeydown);
    },
  };
}
